<template>
  <SearchInput
      v-if="activityRegistrations.length"
      v-model="registrationsSearchValue"
      class="registrationsHeaders"/>
  <DataTable
      v-if="activityRegistrations.length"
      :headers="registrationHeaders"
      :items="registrationsItems"
      :search-value="registrationsSearchValue"
      @delete="handleDelete"
      @edit="handleEdit"/>
  <p v-else>{{ t('registrations.noRegistrations') }}</p>
</template>

<script lang="ts" setup>
import {useI18n} from "vue3-i18n";
import {computed, onMounted, ref} from "vue";
import {ActivityRegistration, Guid} from "@/types";
import SearchInput from "@/components/layout/SearchInput.vue";
import DataTable from "@/components/layout/DataTable.vue";
import {SucceededOrNotResponse} from "@/types/responses";
import {notifyError, notifySuccess} from "@/notify";
import {useMemberService} from "@/inversify.config";
import i18n from "@/i18n";
import '@/extensions/date.extensions';

// eslint-disable-next-line
const emit = defineEmits<{
  (event: "activityRegistrationSelected", id: string): void
}>()

const {t} = useI18n()
const memberService = useMemberService()

const registrationsSearchValue = ref("");
const activityRegistrations = ref<ActivityRegistration[]>([])

onMounted(async () => {
  await fetchActivityRegistration();
})

const registrationsItems = computed(() => {
  let items: any[] = [];
  if (activityRegistrations.value === null || activityRegistrations.value.length == 0)
    return []

  let lang = i18n.getLocale()

  let now = new Date();
  activityRegistrations.value.forEach((registration: ActivityRegistration) => {
    let activityName = (lang == 'fr' ? registration.activityNameFr : registration.activityNameEn) ?? '';

    let occurrenceStartDateTime = new Date(registration.activityOccurrenceStartDateAndTime ?? '');
    let status = registration.deletedActivityOccurrence
      ? t("activityOccurrences.canceled")
      : occurrenceStartDateTime > now
        ? t("activityOccurrences.upcoming")
        : t("activityOccurrences.completed");

    let actions = {
      edit: !registration.deletedActivityOccurrence,
      delete: registration.deletedActivityOccurrence
        ? false
        : new Date(registration.activityOccurrenceStartDateAndTime ?? "").hoursBetweenToday() >= 48
    }
    let hasNoActions = Object.values(actions).every(v => !v);
    items.push({
      id: registration.id,
      activityName: activityName,
      activityOccurrenceStartDate: occurrenceStartDateTime.formatToDateString(),
      activityOccurrenceStartTime: occurrenceStartDateTime.formatToTimeString(),
      firstName: registration.billingAddress?.firstName,
      lastName: registration.billingAddress?.lastName,
      email: registration.billingAddress?.email,
      participantsCount: registration.participants?.length,
      isPaid: registration.isFree ? t("global.free") : registration.isPaid ? t("global.yes") : t("global.no"),
      status: status,
      actions: hasNoActions ? undefined : actions
    })
  })

  return items
    .sort((a, b) => new Date(b.activityOccurrenceStartDate ?? "").valueOf() - new Date(a.activityOccurrenceStartDate ?? "").valueOf());
})

let actionsHeader = {
  text: t("global.table.actions"),
  value: "actions",
};

const registrationBaseHeaders = [
  {
    text: t("activities.activity"),
    value: "activityName",
    sortable: true,
  },
  {
    text: t("organization.status"),
    value: "status",
    sortable: true
  },
  {
    text: t("activities.form.startDate"),
    value: "activityOccurrenceStartDate",
    sortable: true,
  },
  {
    text: t("activities.form.startsAt"),
    value: "activityOccurrenceStartTime",
    sortable: true,
  },
  {
    text: t("participants.lastName"),
    value: "lastName",
    sortable: true,
  },
  {
    text: t("participants.firstName"),
    value: "firstName",
    sortable: true,
  },
  {
    text: t("participants.participants"),
    value: "participantsCount",
    sortable: true,
  },
  {
    text: t("invoice.isPaid"),
    value: "isPaid",
    sortable: true,
  }
];
const registrationHeaders = computed(() => {
  if (registrationsItems.value.every(item => item.actions === undefined))
    return registrationBaseHeaders;
  return [...registrationBaseHeaders, actionsHeader];
});

async function handleDelete(activityRegistration: any) {
  if (activityRegistration?.id == null || !Guid.isValid(activityRegistration.id ?? ""))
    return;

  let confirmDelete = confirm(t("registrations.form.deleteConfirmation"));
  if (!confirmDelete) return;

  let result: SucceededOrNotResponse = await memberService.deleteCurrentMemberActivityRegistration(activityRegistration.id);

  if (result?.succeeded) {
    notifySuccess(t("registrations.form.deleteSuccess"));
    await fetchActivityRegistration();
  } else {
    notifyError(t("registrations.form.deleteError"));
  }
}

function handleEdit(id: string) {
  emit("activityRegistrationSelected", id)
}

async function fetchActivityRegistration() {
  let response = await memberService.getCurrentMemberActivityRegistrations()
  activityRegistrations.value = response as ActivityRegistration[];
}

</script>